'use client';

import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';

const linkClass =
    'hover:text-[var(--color-gold-bright)] !duration-500 !transition-colors';

const Footer = () => {
    const t = useTranslations('footer');

    const links = t.raw('links') as { title: string; href: string }[];
    const categories = t.raw('categories') as string[];

    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: { staggerChildren: 0.1 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0 },
    };

    return (
        <footer className="bg-[var(--color-dark-navy)] border-t border-[var(--color-white)] text-[var(--color-white)] overflow-hidden">
            <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    variants={containerVariants}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8 sm:gap-10 mb-8 sm:mb-12 text-center sm:text-start"
                >
                    {/* Logo + links */}
                    <motion.div variants={itemVariants} className="min-w-0">
                        <Image
                            src="/imgs/logo-bg.png"
                            alt="Logo"
                            width={120}
                            height={40}
                            className="mb-4 mx-auto sm:mx-0 h-auto w-[100px] sm:w-[120px]"
                        />
                        <ul className="space-y-2">
                            {links.map((link) => (
                                <motion.li key={link.href} variants={itemVariants}>
                                    <Link className={linkClass} href={link.href}>
                                        {link.title}
                                    </Link>
                                </motion.li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Categories */}
                    <motion.div variants={itemVariants} className="min-w-0">
                        <h3 className="text-lg font-semibold mb-4">{t('categoriesTitle')}</h3>
                        <ul className="space-y-2">
                            {categories.map((item, i) => (
                                <motion.li key={i} variants={itemVariants}>
                                    <span className="hover:text-[var(--color-gold-bright)] duration-500 transition-colors">
                                        {item}
                                    </span>
                                </motion.li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Social */}
                    <motion.div variants={itemVariants} className="min-w-0">
                        <h3 className="text-lg font-semibold mb-4">{t('followUs')}</h3>
                        <div className="flex gap-4 justify-center sm:justify-start">
                            <a
                                href="https://www.facebook.com/share/16Tpg7TFSU/?mibextid=wwXIfr"
                                aria-label="Facebook"
                                className={linkClass}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M22.676 0H1.326C.593 0 0 .593 0 1.326v21.348C0 23.407.593 24 1.326 24h11.495v-9.294H9.691V11.01h3.13V8.41c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.464.099 2.796.143v3.24l-1.918.001c-1.504 0-1.796.715-1.796 1.763v2.31h3.587l-.467 3.696h-3.12V24h6.116C23.407 24 24 23.407 24 22.674V1.326C24 .593 23.407 0 22.676 0z" />
                                </svg>
                            </a>
                            <a
                                href="https://www.linkedin.com/company/wafra-platform/"
                                aria-label="LinkedIn"
                                className={linkClass}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M4.98 3.5C3.87 3.5 3 4.38 3 5.48c0 1.09.87 1.97 1.98 1.97 1.1 0 1.98-.88 1.98-1.97 0-1.1-.88-1.98-1.98-1.98zM2 8.98h5.96V22H2zM9.98 8.98h5.72v1.79h.08c.8-1.48 2.76-2.05 4.24-1.23 1.36.74 2.01 2.03 2.01 4.15V22h-5.96v-5.8c0-1.38-.03-3.16-1.93-3.16-1.93 0-2.22 1.51-2.22 3.06V22H9.98z" />
                                </svg>
                            </a>
                        </div>
                    </motion.div>

                    {/* Contact */}
                    <motion.div variants={itemVariants} className="min-w-0">
                        <h3 className="text-lg font-semibold mb-4">{t('contactUs')}</h3>
                        <ul className="space-y-3">
                            <li>
                                <a
                                    href="mailto:info@wafra-platform.com"
                                    className={`${linkClass} break-all`}
                                >
                                    info@wafra-platform.com
                                </a>
                                <p className="text-sm text-gray-300">{t('generalInquiries')}</p>
                            </li>
                            <li>
                                <a
                                    href="mailto:support@wafra-platform.com"
                                    className={`${linkClass} break-all`}
                                >
                                    support@wafra-platform.com
                                </a>
                                <p className="text-sm text-gray-300">{t('support')}</p>
                            </li>
                        </ul>
                    </motion.div>

                    {/* Payment methods */}
                    <motion.div variants={itemVariants} className="min-w-0">
                        <h3 className="text-lg font-semibold mb-4">{t('paymentMethods')}</h3>
                        <div className="flex flex-wrap gap-2 items-center justify-center sm:justify-start">
                            {[
                                { src: '/imgs/paymob.png', alt: 'Paymob' },
                                { src: '/imgs/visa.png', alt: 'Visa' },
                                { src: '/imgs/mastercard.png', alt: 'Mastercard' },
                            ].map((p) => (
                                <div
                                    key={p.alt}
                                    className="p-2 rounded-lg shadow-xl w-16 h-10 flex items-center justify-center bg-white"
                                >
                                    <Image
                                        src={p.src}
                                        alt={p.alt}
                                        width={64}
                                        height={32}
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    className="text-center text-[var(--color-gold-bright)] text-sm sm:text-base pt-6 border-t border-white/10"
                >
                    <p>{t('rights')}</p>
                </motion.div>
            </div>
        </footer>
    );
};

export default Footer;